import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import Container from '@material-ui/core/Container';
import CircularProgress from '@material-ui/core/CircularProgress';
import {Animated} from 'react-animated-css';
import {Redirect} from 'react-router-dom';

import PageHeaderAtm from '../../atomic/PageHeader/PageHeader';
import {PageTitle} from '../../atomic/PageHeader/PageTitle';
import {CONTENT, NETWORK_INVENTORY_SHORT} from '../../../constants/appUrls.constants';
import {getPropertyName} from '../../../utils';
import {isWebSettingsAllowed} from '../Web/WebSettings/allowedEmails';

import {getReservationList, getReservationListFilter} from '../../../redux/actions/Network/Reservation/reservationList.action';
import {getPropertyInventory} from '../../../redux/actions/Network/Inventory/inventoryAction';
import {getProfile} from '../../../redux/actions/User/Profile/profileAction';

import panelStyles, {CHANNEL_COLORS} from './panelStyles';
import NowTiles from './components/NowTiles';
import TrendChart from './components/TrendChart';
import PeriodSummary from './components/PeriodSummary';
import ChannelTable from './components/ChannelTable';
import RecentBookings from './components/RecentBookings';
import AvailabilityCard from './components/AvailabilityCard';
import MonthPicker from './components/MonthPicker';
import {
    todaysBookings,
    todaysMovements,
    channelBreakdown,
    periodSummary,
    dailyTrend,
    latestBookings,
    availabilitySummary,
    bookingsInRange,
    sumByCurrency,
    primaryCurrency,
    earnedOnly,
    pendingOnly,
    formatMoney,
    BOOKING_STATUS,
} from './panelMetrics';

// Kontenjan Booking Engine kanalindan okunur (Kontenjan ekrani da /inventory/1 kullanir)
const BOOKING_ENGINE_CHANNEL_ID = 1;

// Kontenjan penceresi takvimde cizilen ay sayisiyla ayni olmali: kartta
// "X / Y gun" yaziyorsa kullanici o gunleri takvimde sayabilmeli.
// AvailabilityCard 3 ay ciziyor, biz de 3. ayin sonuna kadar cekiyoruz.
const INVENTORY_MONTHS = 3;

// Icinde bulunulan ayda trend son 30 gunu gosterir; liste bu pencereyi
// kapsayacak kadar geriden baslar. Gecmis bir ay secilirse trend o ayin gunleri olur.
const TREND_DAYS = 30;

// Ay secicinin gerisi: 24 ay (icinde bulunulan ay dahil)
const MONTH_HISTORY = 23;

export default function Panel() {
    const {t} = useTranslation();
    const classes = panelStyles();
    const dispatch = useDispatch();

    const userEmail = useSelector((state) => state.profileReducer
        && state.profileReducer.profileData
        && state.profileReducer.profileData.profile
        && state.profileReducer.profileData.profile.email);

    const reservationReducer = useSelector((state) => state.reservationListReducer);
    const firstBookingDate = useSelector((state) => state.reservationListReducer
        && state.reservationListReducer.reservationListFilter
        && state.reservationListReducer.reservationListFilter.first_booking_date);
    const inventoryData = useSelector((state) => state.inventoryReducer?.inventory);
    const inventoryFetching = useSelector((state) => state.inventoryReducer?.fetching);

    // Secili donem 'YYYY-MM' anahtariyla tutulur; moment nesnesi mutable oldugu
    // icin state'te string durur, moment'e turetilen yerde cevrilir.
    const [monthKey, setMonthKey] = useState(() => moment().format('YYYY-MM'));

    const period = useMemo(() => {
        const start = moment(monthKey, 'YYYY-MM').startOf('month');
        const isCurrent = start.isSame(moment(), 'month');
        return {
            start,
            end: isCurrent ? moment().endOf('day') : start.clone().endOf('month'),
            isCurrent,
            // Gecmis yildaysa etiket yili da tasisin
            label: start.isSame(moment(), 'year') ? start.format('MMMM') : start.format('MMMM YYYY'),
        };
    }, [monthKey]);

    // Donemden bagimsiz olanlar yalnizca mount'ta
    useEffect(() => {
        document.body.style.backgroundColor = '#f2f5fa';
        dispatch(getProfile());

        // Kontenjan bugunden ileri bakar, secili aydan etkilenmez; tek sefer cekilir
        dispatch(getPropertyInventory(
            moment().format('YYYY-MM-DD'),
            moment().add(INVENTORY_MONTHS - 1, 'months').endOf('month').format('YYYY-MM-DD'),
            BOOKING_ENGINE_CHANNEL_ID
        ));

        // Ay secicinin alt sinirini belirlemek icin: first_booking_date
        dispatch(getReservationListFilter());
    }, [dispatch]);

    // Rezervasyon listesi secili aya baglidir; ay degisince yeniden cekilir.
    useEffect(() => {
        const start = moment(monthKey, 'YYYY-MM').startOf('month');
        const isCurrent = start.isSame(moment(), 'month');

        // Icinde bulunulan ayda trend 30 gun geriye bakar; ayin ilk gunlerinde
        // liste ay basindan degil, o pencerenin basindan baslamali.
        const from = isCurrent
            ? moment.min(start, moment().subtract(TREND_DAYS - 1, 'days'))
            : start;
        const to = isCurrent ? moment() : start.clone().endOf('month');

        dispatch(getReservationList({
            date_type: 'reservation_time',
            date_range: `${from.format('YYYY-MM-DD')} ${to.format('YYYY-MM-DD')}`,
        }));
    }, [dispatch, monthKey]);

    const fetching = reservationReducer && reservationReducer.fetching;
    const bookings = useMemo(() => {
        const list = reservationReducer && reservationReducer.reservationList;
        return (list && Array.isArray(list.booking)) ? list.booking : [];
    }, [reservationReducer]);

    // --- Turetilmis metrikler ---
    const monthBookings = useMemo(
        () => bookingsInRange(bookings, period.start, period.end),
        [bookings, period]
    );

    // Icinde bulunulan ayda akis 30 gunluk pencereyi kullanir (ayin 2'sinde
    // iki noktali grafik ise yaramaz); gecmis ayda pencere o ayin kendisidir.
    const feed = period.isCurrent ? bookings : monthBookings;

    const today = useMemo(() => todaysBookings(bookings), [bookings]);
    const todayActive = useMemo(
        () => today.filter((b) => Number(b.status) !== BOOKING_STATUS.CANCELLED),
        [today]
    );
    const todayCancelled = useMemo(
        () => today.filter((b) => Number(b.status) === BOOKING_STATUS.CANCELLED),
        [today]
    );

    const movements = useMemo(() => todaysMovements(bookings), [bookings]);
    // Bugunku ciro on rezervasyonu icermez; adet ("Yeni rezervasyon") icerir
    const todayRevenue = useMemo(
        () => primaryCurrency(sumByCurrency(earnedOnly(todayActive))),
        [todayActive]
    );
    const todayPending = useMemo(() => pendingOnly(todayActive).length, [todayActive]);
    const summary = useMemo(() => periodSummary(monthBookings), [monthBookings]);
    const channels = useMemo(() => channelBreakdown(monthBookings), [monthBookings]);
    const trend = useMemo(() => dailyTrend(
        feed,
        period.isCurrent ? TREND_DAYS : period.start.daysInMonth(),
        period.end
    ), [feed, period]);
    const recent = useMemo(() => latestBookings(feed, 10), [feed]);

    // Gecmis ayda "bugun" kutusunun yerini donemin en yogun gunu alir
    const peakDay = useMemo(() => {
        const best = trend.points.reduce((a, b) => (b.count > a.count ? b : a), {count: 0});
        return best.count > 0 ? best : null;
    }, [trend]);
    const availability = useMemo(() => availabilitySummary(inventoryData), [inventoryData]);

    const nowProps = {
        today: todayActive,
        movements,
        revenue: todayRevenue,
        cancelledToday: {
            count: todayCancelled.length,
            revenue: primaryCurrency(sumByCurrency(todayCancelled)),
        },
        pendingToday: todayPending,
    };

    // Kanal rengi listede ve tabloda ayni olsun: ad -> renk eslemesi tek yerden
    const channelColorOf = useMemo(() => {
        const map = new Map();
        // Renkler tabloda gorunen kanallara gore dagitilir. Meta satirlari da
        // sayilsaydi tablodaki nokta ile listedeki avatar farkli renk olurdu;
        // meta kayitlari notr griye duser.
        channels
            .filter((row) => !row.isMeta)
            .forEach((row, index) => map.set(row.name, CHANNEL_COLORS[index] || '#8a95a5'));
        return (name) => map.get(name) || '#8a95a5';
    }, [channels]);


    // Kademeli acilis: buton gizli olsa da adres cubugundan girilebildigi icin
    // sayfa da ayni listeyle korunur (Site Ayarlari ekraniyla tek kaynak).
    // E-posta gelmeden karar verilmez; o sirada yukleme gorunur.
    if (userEmail && !isWebSettingsAllowed(userEmail)) {
        return <Redirect to={CONTENT} />;
    }

    const propertyName = getPropertyName();

    // Secici verinin basladigi aydan geriye gitmesin: tesisin ilk rezervasyonu
    // backend'den geliyor (first_booking_date). Gelmezse sabit gerilemeye duser.
    const fallbackMin = moment().subtract(MONTH_HISTORY, 'months');
    const firstMonth = firstBookingDate ? moment(firstBookingDate, 'YYYY-MM-DD') : null;
    const minMonth = (firstMonth && firstMonth.isValid() && firstMonth.isAfter(fallbackMin)
        ? firstMonth
        : fallbackMin).format('YYYY-MM');

    return (
        <Fragment>
            <PageTitle title={t('enw-panel-page_title', 'Panel')} />
            <PageHeaderAtm
                exitText={t('enw-product_one-title')}
                backButton={CONTENT}
                text={t('enw-panel-page_title', 'Panel')}
            />

            <Animated animationIn="fadeIn" animationInDuration={600} isVisible={true}>
                <Container maxWidth="xl">
                    <div className="container-width pb-0 mb-24">
                        <div className={classes.layout}>

                            <div className={classes.pageHead}>
                                <div>
                                    {/* Otel adi basligin ustunde: uzun adlar 27px baslikta
                                        satir kirar, ayrica "hangi tesis" sorusu baglam
                                        bilgisi — basligin kendisi degil. */}
                                    {propertyName && (
                                        <div className={classes.pageEyebrow}>{propertyName}</div>
                                    )}
                                    <h1 className={classes.pageTitle}>
                                        {t('enw-panel-heading', 'Rezervasyon Paneli')}
                                    </h1>
                                </div>
                                <MonthPicker
                                    value={monthKey}
                                    onChange={setMonthKey}
                                    minMonth={minMonth}
                                    maxMonth={moment().format('YYYY-MM')}
                                />
                            </div>

                            {fetching ? (
                                <div className={classes.loadingBox}>
                                    <CircularProgress size={32} style={{color: '#d71f26'}} />
                                </div>
                            ) : (
                                <Fragment>

                                    {/* Bugunun operasyonel ozeti; gecmis bir ay secildiginde
                                        gosterilmez, yoksa donemle alakasiz bir blok olarak kalir. */}
                                    {period.isCurrent && <NowTiles {...nowProps} />}

                                    {/* Musaitlik bugunden ileriye bakar, secili donemle ilgisi yok.
                                        Kural: bugune ait bloklar yalnizca bugunun ayinda gorunur —
                                        yoksa "Ocak 2026" basligi altinda Eylul takvimi duruyor. */}
                                    {period.isCurrent && (
                                        <AvailabilityCard
                                            availability={availability}
                                            fetching={inventoryFetching}
                                            inventoryUrl={`${NETWORK_INVENTORY_SHORT}/${BOOKING_ENGINE_CHANNEL_ID}`}
                                        />
                                    )}

                                    <div className={classes.main}>
                                        <section className={classes.card}>
                                            <header className={classes.cardHead}>
                                                <div>
                                                    <h2 className={classes.cardTitle}>
                                                        {t('enw-panel-trend_title', 'Rezervasyon Akışı')}
                                                    </h2>
                                                    <p className={classes.cardDesc}>
                                                        {period.isCurrent
                                                            ? t('enw-panel-trend_desc', 'Son 30 gün · günlük rezervasyon adedi')
                                                            : t('enw-panel-trend_desc_period', {
                                                                defaultValue: '{{period}} · günlük rezervasyon adedi',
                                                                period: period.label,
                                                            })}
                                                    </p>
                                                </div>
                                                {/* Icinde bulunulan ayda grafik 30 gunluk pencereyi cizer,
                                                    buyuk sayi ise ayin toplami — ikisi ayni satirda farkli
                                                    cikabildigi icin notun penceresi acikca yaziliyor. */}
                                                <span className={classes.cardNote}>
                                                    {period.isCurrent
                                                        ? t('enw-panel-trend_note_30', {
                                                            defaultValue: 'Son 30 günde toplam {{total}} · günlük ort. {{avg}}',
                                                            total: trend.total,
                                                            avg: trend.average.toFixed(1).replace('.', ','),
                                                        })
                                                        : t('enw-panel-trend_note', {
                                                            defaultValue: 'Toplam {{total}} · günlük ort. {{avg}}',
                                                            total: trend.total,
                                                            avg: trend.average.toFixed(1).replace('.', ','),
                                                        })}
                                                </span>
                                            </header>
                                            <div className={classes.cardBody} style={{padding: '16px 18px 10px'}}>
                                                <div className={classes.occRow}>
                                                    <div className={classes.occNumber}>
                                                        {summary.count}
                                                    </div>
                                                    <div className={classes.occMeta}>
                                                        <div className={classes.occKey}>
                                                            {t('enw-panel-month_bookings', {
                                                                defaultValue: '{{period}} ayında gelen rezervasyon',
                                                                period: period.label,
                                                            })}
                                                        </div>
                                                        <div className={classes.occValue}>
                                                            {summary.revenue.code
                                                                ? `${formatMoney(summary.revenue.amount)} ${summary.revenue.code}`
                                                                : '—'}
                                                        </div>
                                                    </div>
                                                    {/* Canli donemde bugun, kapanmis donemde en yogun gun */}
                                                    <div className={classes.occMeta} style={{marginLeft: 'auto', textAlign: 'right'}}>
                                                        <div className={classes.occKey}>
                                                            {period.isCurrent
                                                                ? t('enw-panel-today_short', 'Bugün')
                                                                : t('enw-panel-peak_day', 'En yoğun gün')}
                                                        </div>
                                                        <div className={classes.occValue}>
                                                            {period.isCurrent && (
                                                                <>{todayActive.length} {t('enw-panel-bookings_short', 'rezervasyon')}</>
                                                            )}
                                                            {!period.isCurrent && peakDay && (
                                                                <>
                                                                    {peakDay.date.format('D MMMM')}
                                                                    {' · '}
                                                                    {peakDay.count} {t('enw-panel-bookings_short', 'rezervasyon')}
                                                                </>
                                                            )}
                                                            {!period.isCurrent && !peakDay && '—'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={classes.divider}>
                                                    <TrendChart trend={trend} />
                                                </div>
                                            </div>
                                        </section>

                                        <PeriodSummary summary={summary} periodLabel={period.label} partial={period.isCurrent} />
                                    </div>

                                    <ChannelTable channels={channels} />

                                    <RecentBookings bookings={recent} channelColorOf={channelColorOf} periodLabel={period.isCurrent ? null : period.label} />
                                </Fragment>
                            )}
                        </div>
                        <style>{`
                            .MuiPickersMonthSelection-container {
                                width:100%!important;
                            }
                            .MuiPickersBasePicker-pickerView {
                                max-width:100%!important;
                                width:100%!important;
                            }
                            .MuiTypography-subtitle1:hover {
                                color: #d71f26 !important;
                            }
                            /* Takvimdeki secili ay/yil ve dialog butonlari kurum rengine */
                            .MuiPickersMonth-monthSelected,
                            .MuiPickersYear-yearSelected {
                                color: #d71f26 !important;
                            }
                            .MuiPickersModal-dialogRoot .MuiButton-textPrimary {
                                color: #d71f26 !important;
                            }
                            .MuiPickersToolbarText-toolbarTxt:hover {
                                color: #fff !important;
                            }
                            .MuiPickersYearSelection-container {
                                height:auto !important;
                            }
                            .MuiCircularProgress-indeterminate {
                                position: relative !important;
                                top: 25vh !important;
                            }
                        `}</style>
                    </div>
                </Container>
            </Animated>
        </Fragment>
    );
}
